import { useEffect, useState } from "react";
import { api, type ActivityResponse } from "@shared/api";

const CURRENT_YEAR = new Date().getFullYear();

interface UseActivityCalendarDataResult {
  data: ActivityResponse | null;
  error: string | null;
  hasPreviousYear: boolean;
  hasNextYear: boolean;
}

export function useActivityCalendarData(year: number, refreshKey: number): UseActivityCalendarDataResult {
  const [data, setData] = useState<ActivityResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasPreviousYear, setHasPreviousYear] = useState(false);
  const [hasNextYear, setHasNextYear] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setHasPreviousYear(false);
    setHasNextYear(false);

    const from = `${year}-01-01`;
    const to = `${year}-12-31`;
    const previousYear = year - 1;
    const nextYear = year + 1;

    void Promise.all([
      api.activity(from, to),
      api.activity(`${previousYear}-01-01`, `${previousYear}-12-31`),
      nextYear <= CURRENT_YEAR ? api.activity(`${nextYear}-01-01`, `${nextYear}-12-31`) : Promise.resolve(null),
    ])
      .then(([currentData, previousData, nextData]) => {
        if (cancelled) return;
        setData(currentData);
        setHasPreviousYear(hasAnyActivity(previousData.days));
        setHasNextYear(nextData ? hasAnyActivity(nextData.days) : false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      });

    return () => {
      cancelled = true;
    };
  }, [refreshKey, year]);

  return { data, error, hasPreviousYear, hasNextYear };
}

function hasAnyActivity(days: ActivityResponse["days"]): boolean {
  return days.some((day) => day.minutes > 0 || day.matches > 0);
}
